import React, { useState, useRef, useEffect } from 'react';

/**
 * Chip component showing required forms on a task.
 * Shows a count badge, and clicking opens a popover listing each form
 * with an "Open & Fill" action button.
 */

interface RequiredForm {
  form_number: string;
  form_name: string;
  is_mandatory: boolean;
}

interface FormRequiredChipProps {
  forms: RequiredForm[];
  onFillForm?: (formNumber: string) => void;
}

const FormRequiredChip: React.FC<FormRequiredChipProps> = ({ forms, onFillForm }) => {
  const [isOpen, setIsOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Close popover on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  if (forms.length === 0) return null;

  return (
    <div className="relative inline-block" ref={popoverRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-lex-accent-soft text-lex-accent hover:bg-lex-state-hover transition-colors"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
        </svg>
        {forms.length} Form{forms.length !== 1 ? 's' : ''}
      </button>

      {/* Popover */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-72 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3 left-0">
          <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
            Required Forms
          </h4>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {forms.map((form) => (
              <div
                key={form.form_number}
                className="flex items-center justify-between gap-2 p-2 rounded bg-gray-50 dark:bg-gray-700/50"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {form.form_number}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {form.form_name}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {form.is_mandatory && (
                    <span className="text-xs text-red-600 dark:text-red-400 font-medium">
                      Req
                    </span>
                  )}
                  {onFillForm && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onFillForm(form.form_number);
                      }}
                      className="px-2 py-1 text-xs bg-lex-accent hover:bg-lex-accent-hover text-lex-accent-contrast rounded transition-colors"
                    >
                      Open &amp; Fill
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FormRequiredChip;
